import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiHelpCircle, FiX } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

function HelpTooltip({ content, title, position = 'top' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobileModalOpen, setIsMobileModalOpen] = useState(false);

  const isMobile = () => {
    return window.innerWidth < 768;
  };

  const handleClick = () => {
    if (isMobile()) {
      setIsMobileModalOpen(true);
    } else {
      setIsOpen(!isOpen);
    }
  };

  const handleMouseEnter = () => {
    if (!isMobile()) {
      setIsOpen(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile()) {
      setIsOpen(false);
    }
  };

  const getTooltipPosition = () => {
    switch (position) {
      case 'top':
        return 'bottom-full left-1/2 transform -translate-x-1/2 mb-2';
      case 'bottom':
        return 'top-full left-1/2 transform -translate-x-1/2 mt-2';
      case 'left':
        return 'right-full top-1/2 transform -translate-y-1/2 mr-2';
      case 'right':
        return 'left-full top-1/2 transform -translate-y-1/2 ml-2';
      default:
        return 'bottom-full left-1/2 transform -translate-x-1/2 mb-2';
    }
  };

  return (
    <>
      <div className="relative inline-block">
        <button
          onClick={handleClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          className="text-text-muted hover:text-text-secondary transition-colors duration-200 p-1 rounded-full hover:bg-surface"
        >
          <SafeIcon icon={FiHelpCircle} className="w-4 h-4" />
        </button>

        {/* Desktop Tooltip */}
        <AnimatePresence>
          {isOpen && !isMobile() && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className={`absolute z-50 ${getTooltipPosition()}`}
            >
              <div className="bg-text-primary text-white text-sm rounded-xl shadow-card p-3 w-64 min-w-[200px]">
                {title && (
                  <div className="font-bold mb-1">{title}</div>
                )}
                <div className="text-stone-100">{content}</div>
                {/* Tooltip arrow */}
                <div className={`absolute w-2 h-2 bg-text-primary transform rotate-45 ${
                  position === 'top' ? 'top-full left-1/2 -translate-x-1/2 -mt-1' :
                  position === 'bottom' ? 'bottom-full left-1/2 -translate-x-1/2 -mb-1' :
                  position === 'left' ? 'left-full top-1/2 -translate-y-1/2 -ml-1' :
                  'right-full top-1/2 -translate-y-1/2 -mr-1'
                }`}></div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Mobile Modal */}
      <AnimatePresence>
        {isMobileModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={() => setIsMobileModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-card rounded-xl shadow-modal p-6 max-w-sm w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                {title && (
                  <h3 className="text-lg font-bold text-text-primary">{title}</h3>
                )}
                <button
                  onClick={() => setIsMobileModalOpen(false)}
                  className="text-text-muted hover:text-text-secondary transition-colors duration-200"
                >
                  <SafeIcon icon={FiX} className="w-5 h-5" />
                </button>
              </div>
              <div className="text-text-secondary leading-relaxed">
                {content}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default HelpTooltip;